// ─── MobileHealthCheckButton ─────────────────────────────────
// Phase 1.7 + 1.7.1. On-demand mobile-layout sanity check for owners/admins.
// Opens a modal that loads the LIVE public invitation in an off-screen
// iframe at iPhone width, runs read-only DOM checks, and shows a clean
// summary of any issues. Advisory only — does NOT block publish.
//
// 1.7.1: when the `not_section_v1` issue is detected, surface an inline
// "Enable now" action. Clicking it calls the existing safe migrate endpoint
// (immutable backup is preserved by the backend) and re-runs the check.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, X, CheckCircle2, AlertTriangle, AlertCircle, Info, Loader2,
} from 'lucide-react';
import { buildPublicPreviewUrl } from './PhonePreviewModal';
import { runMobileLayoutHealthCheck, SEVERITY } from '../../lib/utils/mobileLayoutHealthCheck';

const API = process.env.REACT_APP_BACKEND_URL;

function formatBytes(bytes) {
  if (!bytes || bytes < 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const SEVERITY_META = {
  [SEVERITY.ERROR]:   { Icon: AlertCircle,    color: 'text-red-600',    label: 'Issue' },
  [SEVERITY.WARNING]: { Icon: AlertTriangle,  color: 'text-amber-600',  label: 'Warning' },
  [SEVERITY.INFO]:    { Icon: Info,           color: 'text-blue-500',   label: 'Hint' },
};

function HealthCheckModal({
  slug,
  eventId,
  layoutVersion: initialLayoutVersion,
  layoutMode,
  isPublished,
  previewToken,
  onClose,
  onLayoutVersionChange,
  onSelectElement,
  onSnapToEdges,
}) {
  const iframeRef = useRef(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [result, setResult] = useState(null);
  const [layoutVersion, setLayoutVersion] = useState(initialLayoutVersion);
  const [migrating, setMigrating] = useState(false);
  const [reloadKey, setReloadKey] = useState(0); // bump to force iframe reload

  // Action state for "Optimize for mobile" and "Add missing dimensions"
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeResult, setOptimizeResult] = useState(null); // { savedBytes, count, mapping, failures }
  const [savingDims, setSavingDims] = useState(false);
  const [dimsResult, setDimsResult] = useState(null); // { added }
  const [undoTimerId, setUndoTimerId] = useState(null);
  const [undoVisible, setUndoVisible] = useState(false);

  const url = buildPublicPreviewUrl({ slug, isPublished, previewToken });

  // Close on Esc
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Run the check after the iframe settles. We give it up to 4.5s.
  useEffect(() => {
    if (!url) {
      setStatus('error');
      setResult(runMobileLayoutHealthCheck({ iframeDoc: null, layoutVersion, layoutMode }));
      return undefined;
    }
    setStatus('loading');
    let cancelled = false;
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    const runChecks = () => {
      if (cancelled) return;
      let doc = null;
      try {
        doc = iframe.contentDocument || iframe.contentWindow?.document || null;
      } catch {
        doc = null;
      }
      const res = runMobileLayoutHealthCheck({ iframeDoc: doc, layoutVersion, layoutMode });
      setResult(res);
      setStatus(doc ? 'ready' : 'error');
    };

    const onLoad = () => setTimeout(runChecks, 1500);
    iframe.addEventListener('load', onLoad);
    const ceiling = setTimeout(runChecks, 4500);

    return () => {
      cancelled = true;
      clearTimeout(ceiling);
      iframe.removeEventListener('load', onLoad);
    };
  }, [url, layoutVersion, layoutMode, reloadKey]);

  const handleEnableNow = useCallback(async () => {
    if (!eventId || migrating) return;
    setMigrating(true);
    try {
      const res = await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/migrate-to-section-v1`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const newVersion = res.data?.layout_version || 'section-v1';
      setLayoutVersion(newVersion);
      onLayoutVersionChange?.(newVersion);
      toast.success('Mobile optimization enabled. Rechecking layout…');
      // Force the iframe to reload so the public page picks up the new renderer.
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed — please try again');
    } finally {
      setMigrating(false);
    }
  }, [eventId, migrating, onLayoutVersionChange]);

  // ─── Optimize for mobile ────────────────────────────────────
  // Calls /images/optimize with only the URLs the checker flagged. The
  // backend re-runs them through the standard optimization pipeline and
  // rewrites the rsvp_page references to the new (smaller) URLs.
  const handleOptimize = useCallback(async (urls) => {
    if (!eventId || optimizing || !urls?.length) return;
    setOptimizing(true);
    try {
      const res = await axios.post(
        `${API}/api/events/${eventId}/images/optimize`,
        { urls },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const data = res.data || {};
      const realOpt = (data.optimized || []).filter((o) => !o.skipped && (o.bytes_saved || 0) > 0);
      const savedBytes = realOpt.reduce((acc, o) => acc + (o.bytes_saved || 0), 0);
      const rewritesApplied = typeof data.rewrites_applied === 'number' ? data.rewrites_applied : null;
      // QA-requested diagnostic: if any output is still above the
      // checker's MP threshold, surface it loudly so the user isn't
      // told "success" while the warning will obviously re-trip.
      const stillOver = (data.optimized || []).some((o) => o.above_checker_threshold === true);
      setOptimizeResult({
        count: realOpt.length,
        savedBytes,
        mapping: data.mapping || [],
        failures: data.failed || [],
        rewritesApplied,
        stillOver,
      });
      if (realOpt.length > 0) {
        if (rewritesApplied === 0) {
          toast.warning('Optimized, but the invitation still points at the original image. Try re-uploading the image directly.');
        } else if (stillOver) {
          toast.warning('Optimized, but image is still above the mobile threshold. Try re-uploading a smaller copy.');
        } else {
          toast.success(`Optimized ${realOpt.length} image${realOpt.length === 1 ? '' : 's'} · saved ${formatBytes(savedBytes)}`);
        }
        setUndoVisible(true);
        if (undoTimerId) clearTimeout(undoTimerId);
        const tid = setTimeout(() => setUndoVisible(false), 10000);
        setUndoTimerId(tid);
      } else if ((data.optimized || []).length > 0) {
        toast.success('Images already efficient — no changes needed');
      }
      if ((data.failed || []).length > 0) {
        toast.warning(`Could not optimize ${data.failed.length} image${data.failed.length > 1 ? 's' : ''}`);
      }
      setReloadKey((k) => k + 1); // re-render iframe so new URLs are loaded
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Optimization failed — please try again');
    } finally {
      setOptimizing(false);
    }
  }, [eventId, optimizing, undoTimerId]);

  const handleUndoOptimize = useCallback(async () => {
    if (!eventId || !optimizeResult?.mapping?.length) return;
    try {
      await axios.post(
        `${API}/api/events/${eventId}/images/optimize/undo`,
        { mapping: optimizeResult.mapping },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      toast.success('Reverted to original images');
      setOptimizeResult(null);
      setUndoVisible(false);
      if (undoTimerId) clearTimeout(undoTimerId);
      setUndoTimerId(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Undo failed');
    }
  }, [eventId, optimizeResult, undoTimerId]);

  // ─── Add missing dimensions ─────────────────────────────────
  const handleAddDimensions = useCallback(async (rawImages) => {
    if (!eventId || savingDims || !rawImages?.length) return;
    setSavingDims(true);
    // Probe intrinsic dimensions for any image that hasn't loaded yet (lazy
    // images report naturalWidth=0 at check time). We load each src in a
    // detached <img> and read naturalWidth/Height. Fails resolved as null
    // and dropped.
    const probed = await Promise.all(
      rawImages.map((it) => new Promise((resolve) => {
        if (!it?.src) { resolve(null); return; }
        if (it.width > 0 && it.height > 0) {
          resolve({ url: it.src, width: it.width, height: it.height });
          return;
        }
        const probe = new Image();
        let settled = false;
        const done = (entry) => { if (!settled) { settled = true; resolve(entry); } };
        probe.onload = () => done({ url: it.src, width: probe.naturalWidth, height: probe.naturalHeight });
        probe.onerror = () => done(null);
        // Hard ceiling so a single broken URL can't block the action.
        setTimeout(() => done(null), 5000);
        probe.src = it.src;
      })),
    );
    const entries = probed.filter((e) => e && e.width > 0 && e.height > 0);
    const dropped = rawImages.length - entries.length;
    if (entries.length === 0) {
      toast.error('Could not read any image dimensions');
      setSavingDims(false);
      return;
    }
    try {
      const res = await axios.post(
        `${API}/api/events/${eventId}/images/dimensions`,
        { entries },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const added = res.data?.added || 0;
      setDimsResult({ added });
      toast.success(`Added dimensions to ${added} image${added === 1 ? '' : 's'}`);
      if (dropped > 0) {
        toast.warning(`Could not read dimensions for ${dropped} image${dropped === 1 ? '' : 's'}`);
      }
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save image dimensions');
    } finally {
      setSavingDims(false);
    }
  }, [eventId, savingDims]);

  const verdictOk = !!result?.ok && (result?.issues || []).every(
    (i) => i.severity !== SEVERITY.ERROR && i.severity !== SEVERITY.WARNING,
  );

  return (
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="mobile-health-check-modal"
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
          aria-label="Close"
          data-testid="mobile-health-check-close"
        >
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-base font-semibold text-gray-900 mb-1">Mobile layout check</h3>
        <p className="text-xs text-gray-500 mb-4">
          We open the invitation at iPhone size and look for things that often hurt mobile display. Advisory only — nothing is changed.
        </p>

        {/* Off-screen iframe */}
        <div aria-hidden style={{ position: 'fixed', left: -10000, top: 0, width: 390, height: 800, pointerEvents: 'none' }}>
          {url && (
            <iframe
              ref={iframeRef}
              src={`${url}${url.includes('?') ? '&' : '?'}hc=${reloadKey}`}
              title="Mobile health check"
              width={390}
              height={800}
              style={{ border: 0 }}
              data-testid="mobile-health-check-iframe"
            />
          )}
        </div>

        {/* Verdict */}
        <div className="mb-4">
          {status === 'loading' && (
            <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="health-check-loading">
              <Loader2 className="w-4 h-4 animate-spin" />
              Checking the live invitation…
            </div>
          )}
          {status !== 'loading' && result && (
            <div
              className={`flex items-start gap-2 p-3 rounded-lg ${verdictOk ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-900'}`}
              data-testid="health-check-verdict"
            >
              {verdictOk ? <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
              <div className="flex-1">
                <div className="text-sm font-medium">{result.summary}</div>
                <button
                  onClick={() => {
                    setStatus('loading');
                    setOptimizeResult(null);
                    setDimsResult(null);
                    setUndoVisible(false);
                    if (undoTimerId) clearTimeout(undoTimerId);
                    setUndoTimerId(null);
                    setReloadKey((k) => k + 1);
                  }}
                  className="mt-1 text-[11px] underline opacity-70 hover:opacity-100"
                  data-testid="health-check-run-again"
                >
                  Run again
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Issue list */}
        {result?.issues?.length > 0 && (
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="health-check-issues">
            {result.issues.map((issue, i) => {
              const meta = SEVERITY_META[issue.severity] || SEVERITY_META[SEVERITY.INFO];
              const { Icon } = meta;
              const isMigrateable = issue.code === 'not_section_v1' && !!eventId;
              const isHeavyImage = issue.code === 'heavy_image' && !!eventId && (issue.images || []).length > 0;
              const isMissingDims = issue.code === 'images_missing_dimensions' && !!eventId && (issue.images || []).length > 0;
              const isOutOfBounds = issue.code === 'element_out_of_bounds' && (issue.elements || []).length > 0;
              return (
                <div
                  key={i}
                  className="flex items-start gap-2 text-xs text-gray-700"
                  data-testid={`health-issue-${issue.code}`}
                >
                  <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${meta.color}`} />
                  <div className="flex-1">
                    <div>{issue.message}</div>
                    {isMigrateable && (
                      <button
                        onClick={handleEnableNow}
                        disabled={migrating}
                        className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                        data-testid="health-enable-now-btn"
                      >
                        {migrating ? <><Loader2 className="w-3 h-3 animate-spin" /> Enabling…</> : 'Enable now'}
                      </button>
                    )}
                    {isHeavyImage && !optimizeResult && (
                      <button
                        onClick={() => handleOptimize((issue.images || []).map((it) => it.src))}
                        disabled={optimizing}
                        className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                        data-testid="health-optimize-mobile-btn"
                      >
                        {optimizing ? <><Loader2 className="w-3 h-3 animate-spin" /> Optimizing…</> : 'Optimize for mobile'}
                      </button>
                    )}
                    {isHeavyImage && optimizeResult && (
                      <div className="mt-1.5 flex items-center gap-2 text-[11px] text-emerald-700">
                        <CheckCircle2 className="w-3 h-3" />
                        <span data-testid="health-optimize-result">
                          {optimizeResult.count > 0
                            ? `Optimized ${optimizeResult.count} image${optimizeResult.count > 1 ? 's' : ''} · saved ${formatBytes(optimizeResult.savedBytes)}`
                            : 'Already efficient — no changes needed'}
                        </span>
                        {undoVisible && optimizeResult.mapping?.length > 0 && (
                          <button
                            onClick={handleUndoOptimize}
                            className="underline opacity-80 hover:opacity-100"
                            data-testid="health-optimize-undo-btn"
                          >
                            Undo
                          </button>
                        )}
                      </div>
                    )}
                    {isMissingDims && !dimsResult && (
                      <button
                        onClick={() => handleAddDimensions(issue.images || [])}
                        disabled={savingDims}
                        className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                        data-testid="health-add-dimensions-btn"
                      >
                        {savingDims ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> : 'Add missing dimensions'}
                      </button>
                    )}
                    {isMissingDims && dimsResult && (
                      <div className="mt-1.5 flex items-center gap-2 text-[11px] text-emerald-700">
                        <CheckCircle2 className="w-3 h-3" />
                        <span data-testid="health-dimensions-result">
                          Added dimensions to {dimsResult.added} image{dimsResult.added > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                    {isOutOfBounds && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {(issue.elements || []).slice(0, 3).map((entry, idx) => (
                          <React.Fragment key={`${entry.sectionId}-${entry.id}-${idx}`}>
                            {/* Snap to canvas edges — generic across element
                                types (image, shape, text, RSVP button,
                                ornament). Fires whenever the checker tagged
                                this entry with isFullWidthDrift (width ≈100%
                                AND horizontal overflow). Calls the parent
                                normalizer which routes through
                                handleCollageElementResize(..., 1.0); that
                                handler sets x=0.5, xPercent=50, w=1.0,
                                widthPercent=100 → renders left 0% / w 100%.
                                Identical write path the renderer/RSVP/etc
                                already use, so no element-type branching. */}
                            {entry.isFullWidthDrift && entry.sectionId && entry.id && onSnapToEdges && (
                              <button
                                onClick={() => {
                                  onSnapToEdges(entry.sectionId, entry.id);
                                  // Re-run the check so the user sees the verdict update.
                                  setStatus('loading');
                                  setReloadKey((k) => k + 1);
                                }}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-emerald-600 text-white hover:bg-emerald-700"
                                data-testid={`health-snap-to-edges-btn-${idx}`}
                                title={`Snap ${entry.type || 'element'} to canvas edges (left 0% · width 100%)`}
                              >
                                Snap to canvas edges
                              </button>
                            )}
                            {onSelectElement && (
                              <button
                                onClick={() => {
                                  if (!entry.sectionId || !entry.id) return;
                                  onSelectElement(entry.sectionId, entry.id);
                                  onClose?.();
                                }}
                                disabled={!entry.sectionId || !entry.id}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid={`health-select-element-btn-${idx}`}
                                title={entry.id ? `Select ${entry.type}${entry.name ? ` "${entry.name}"` : ''}` : 'Element id missing — cannot select'}
                              >
                                Select {entry.type || 'element'}
                                {(issue.elements || []).length > 1 ? ` #${idx + 1}` : ''}
                              </button>
                            )}
                          </React.Fragment>
                        ))}
                        {(issue.elements || []).length > 3 && (
                          <span className="self-center text-[10px] text-gray-500">
                            +{(issue.elements || []).length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400">
          <span>Renderer: <strong className="text-gray-600">{layoutVersion || 'legacy-flat'}</strong></span>
          {layoutMode && <span>Mode: <strong className="text-gray-600">{layoutMode}</strong></span>}
        </div>
      </div>
    </div>
  );
}

export default function MobileHealthCheckButton({
  slug,
  eventId,
  layoutVersion,
  layoutMode,
  isPublished,
  previewToken,
  onLayoutVersionChange,
  onSelectElement,
  onSnapToEdges,
  variant = 'panel', // 'panel' | 'toolbar'
  adminDarkMode = false,
}) {
  const [open, setOpen] = useState(false);
  if (!slug) return null;

  const toolbarCls =
    'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium hover:bg-gray-100 text-gray-700';
  const panelCls = `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${
    adminDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
  }`;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={variant === 'toolbar' ? toolbarCls : panelCls}
        data-testid="mobile-health-check-btn"
        title="Run a mobile layout check"
      >
        <ShieldCheck className="w-3.5 h-3.5" />
        Check mobile layout
      </button>

      {open && (
        <HealthCheckModal
          slug={slug}
          eventId={eventId}
          layoutVersion={layoutVersion}
          layoutMode={layoutMode}
          isPublished={isPublished}
          previewToken={previewToken}
          onClose={() => setOpen(false)}
          onLayoutVersionChange={onLayoutVersionChange}
          onSelectElement={onSelectElement}
          onSnapToEdges={onSnapToEdges}
        />
      )}
    </>
  );
}
